import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth, useCart } from '../App';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { ShoppingCart, User, Menu, X, ChevronDown, LogOut, ArrowUp, Tag, Copy, Check } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_a3314fd0-818c-4089-8369-a8d30cc8683e/artifacts/o88f67ha_image.png';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { href: '/products', label: 'Products' },
    ...(user ? [{ href: '/shop', label: 'Shop' }] : []),
    ...(user ? [{ href: '/resources', label: 'Resources' }] : []),
    ...(user ? [{ href: '/calculator', label: 'Dosing Guide' }] : []),
    ...(user?.is_wholesale ? [{ href: '/catalog', label: 'Wholesale' }] : []),
    { href: '/coas', label: 'COAs' },
    { href: '/research-insights', label: 'Research Insights' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-slate-200/50">
      <nav className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-28">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Genora Peptides" className="h-24 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-teal-700'
                    : 'text-slate-600 hover:text-teal-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-4">
            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-2 text-slate-600 hover:text-teal-700 transition-colors"
              data-testid="nav-cart"
            >
              <ShoppingCart className="h-5 w-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-teal-700 text-white text-xs flex items-center justify-center font-medium">
                  {itemCount}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2" data-testid="nav-user-menu">
                    <User className="h-5 w-5" />
                    <span className="hidden sm:inline text-sm">{user.name?.split(' ')[0]}</span>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="w-full" data-testid="nav-dashboard">Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="nav-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth" data-testid="nav-login">
                <Button variant="outline" size="sm" className="border-teal-700 text-teal-700 hover:bg-teal-50">
                  Sign In
                </Button>
              </Link>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 text-slate-600"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t border-slate-100">
            <div className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`text-sm font-medium py-2 ${
                    isActive(link.href) ? 'text-teal-700' : 'text-slate-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white">
      {/* Research Disclaimer Banner */}
      <div className="bg-amber-500 py-3">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-sm font-bold text-white uppercase tracking-wider">
            For Research Purposes Only — Not for Human Consumption
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Logo & Description */}
          <div className="col-span-1 md:col-span-2">
            <img src={LOGO_URL} alt="Genora Peptides" className="h-16 w-auto mb-6" />
            <p className="text-slate-400 text-sm leading-relaxed max-w-md">
              Precision research peptides synthesized to the highest purity standards. 
              All products are intended for laboratory research use only.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li><Link to="/products" className="text-slate-400 hover:text-white text-sm transition-colors">Products</Link></li>
              <li><Link to="/coas" className="text-slate-400 hover:text-white text-sm transition-colors">Certificates of Analysis</Link></li>
              <li><Link to="/research-insights" className="text-slate-400 hover:text-white text-sm transition-colors">Research Insights</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Legal</h4>
            <ul className="space-y-3">
              <li><Link to="/privacy-policy" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="text-slate-400 hover:text-white text-sm transition-colors">Terms & Conditions</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Genora LLC. All rights reserved.
            </p>
            <p className="text-slate-400 text-sm">
              Powered by <a href="https://break4styles.com/" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300 transition-colors">www.break4styles.com</a>
            </p>
            <p className="text-slate-500 text-xs">
              Products are not intended to diagnose, treat, cure, or prevent any disease.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Scroll to Top Button
const ScrollToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => {
      if (window.scrollY > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!isVisible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-24 right-6 z-40 p-3 bg-teal-700 hover:bg-teal-800 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110"
      aria-label="Scroll to top"
      data-testid="scroll-to-top-btn"
    >
      <ArrowUp className="h-5 w-5" />
    </button>
  );
};

const AnnouncementBar = () => {
  const { settings } = useSiteSettings();
  const [copied, setCopied] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const enabled = !!settings?.announcement_enabled;
  const message = (settings?.announcement_message || '').trim();
  const code = (settings?.announcement_code || '').trim();
  const start = (settings?.announcement_start || '').trim();
  const end = (settings?.announcement_end || '').trim();

  // Optional schedule: within window (or no bound) when start/end are set.
  const now = Date.now();
  const afterStart = !start || now >= new Date(start).getTime();
  const beforeEnd = !end || now <= new Date(end).getTime();
  const inWindow = afterStart && beforeEnd;

  // Dismissal is per-message: change the message and it re-appears for everyone.
  const dismissKey = `announcement_dismissed:${message}|${code}`;
  useEffect(() => {
    setDismissed(localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);

  if (!enabled || !message || dismissed || !inWindow) return null;

  const copyCode = () => {
    if (!code) return;
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const dismiss = () => {
    localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div
      className="relative bg-gradient-to-r from-teal-700 via-teal-600 to-emerald-600 text-white"
      data-testid="announcement-bar"
      role="region"
      aria-label="Site announcement"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-2.5 flex items-center justify-center gap-x-3 gap-y-1 flex-wrap text-center">
        <Tag className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-medium" data-testid="announcement-message">{message}</span>
        {code && (
          <button
            type="button"
            onClick={copyCode}
            data-testid="announcement-copy-code"
            className="inline-flex items-center gap-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors px-3 py-0.5 text-xs font-semibold tracking-wide"
            title="Copy code"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied!' : `Code: ${code}`}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        data-testid="announcement-dismiss"
        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-white/20 transition-colors"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

const Layout = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <ScrollToTopButton />
    </div>
  );
};

export default Layout;
